/**
 * gamepadUtils - Shared gamepad (game controller) reading helpers.
 *
 * Used by the three human-piloted camera drivers (walk / fly / FPV drone) so
 * they all read a physical controller the same way. Built on the raw
 * `navigator.getGamepads()` Web Gamepad API rather than PlayCanvas's
 * GamepadSource, because we need trigger/button state (jump, sprint, vertical
 * thrust, the hidden mode chord) that GamepadSource's two-Vec2 abstraction
 * does not expose.
 *
 * Everything here assumes the browser's "standard" gamepad mapping, which the
 * overwhelming majority of Xbox / PlayStation / generic pads report. Non-
 * standard pads still populate `axes`/`buttons`, just in a different order —
 * the FPV drone's own calibration is out of scope here; these helpers target
 * the common case that "it just works" for a plugged-in Xbox/PS controller.
 */

/** Standard-mapping button indices (see the W3C Gamepad "standard" layout). */
export const GP_BUTTON = {
    A: 0,          // cross
    B: 1,          // circle
    X: 2,          // square
    Y: 3,          // triangle
    LB: 4,         // left bumper / L1
    RB: 5,         // right bumper / R1
    LT: 6,         // left trigger / L2 (also analog, see readGamepad().lt)
    RT: 7,         // right trigger / R2 (also analog, see readGamepad().rt)
    BACK: 8,       // view / select / share
    START: 9,      // menu / options
    L3: 10,        // left stick click
    R3: 11,        // right stick click
    DPAD_UP: 12,
    DPAD_DOWN: 13,
    DPAD_LEFT: 14,
    DPAD_RIGHT: 15,
} as const;

export interface GamepadState {
    connected: boolean;
    /** Left stick, deadzoned. x = right+, y = up+ (Y already flipped from raw down+). */
    leftX: number;
    leftY: number;
    /** Right stick, deadzoned. x = right+, y = up+ (Y already flipped from raw down+). */
    rightX: number;
    rightY: number;
    /** Analog triggers in 0..1 (fall back to the digital pressed state if the pad reports no analog value). */
    lt: number;
    rt: number;
    /** Raw pressed flags per button index. */
    buttons: boolean[];
    /** The underlying pad, in case a caller needs something bespoke. */
    raw: Gamepad | null;
}

const EMPTY_STATE: GamepadState = {
    connected: false,
    leftX: 0, leftY: 0, rightX: 0, rightY: 0,
    lt: 0, rt: 0,
    buttons: [],
    raw: null,
};

/**
 * Radial deadzone for a single axis. Below `dz` the axis reads 0; above it the
 * remaining travel is rescaled so the stick still reaches full ±1 at the edge
 * (no dead band eating the top of the range).
 */
export function applyAxisDeadzone(value: number, dz: number): number {
    const a = Math.abs(value);
    if (a < dz) return 0;
    return Math.sign(value) * (a - dz) / (1 - dz);
}

/**
 * Return the first connected gamepad's state, deadzoned and normalized, or a
 * disconnected empty state. `deadzone` defaults to a conservative 0.12 that
 * suits worn analog sticks without feeling laggy.
 */
export function readGamepad(deadzone: number = 0.12): GamepadState {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return EMPTY_STATE;
    const pads = navigator.getGamepads();
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
        if (pads[i]) { gp = pads[i]; break; }
    }
    if (!gp) return EMPTY_STATE;

    const axes = gp.axes;
    const btns = gp.buttons;
    const buttons: boolean[] = [];
    for (let i = 0; i < btns.length; i++) buttons[i] = btns[i]?.pressed ?? false;

    // Analog trigger value if the pad exposes it; otherwise fall back to the
    // digital pressed flag so a bumper-less pad still gets full-on/full-off.
    const lt = btns[GP_BUTTON.LT]?.value ?? (buttons[GP_BUTTON.LT] ? 1 : 0);
    const rt = btns[GP_BUTTON.RT]?.value ?? (buttons[GP_BUTTON.RT] ? 1 : 0);

    return {
        connected: true,
        leftX: applyAxisDeadzone(axes[0] ?? 0, deadzone),
        leftY: -applyAxisDeadzone(axes[1] ?? 0, deadzone),
        rightX: applyAxisDeadzone(axes[2] ?? 0, deadzone),
        rightY: -applyAxisDeadzone(axes[3] ?? 0, deadzone),
        lt,
        rt,
        buttons,
        raw: gp,
    };
}

/**
 * Rising-edge detector for gamepad buttons — turns the polled `pressed` flags
 * into one-shot "just pressed this frame" events (for jump, mode toggle, etc.).
 * Each tracker instance keeps its own previous-frame snapshot.
 */
export class GamepadEdgeTracker {
    private prev: boolean[] = [];

    /** True only on the frame `buttons[index]` transitions from up to down. */
    justPressed(buttons: boolean[], index: number): boolean {
        const now = buttons[index] ?? false;
        const was = this.prev[index] ?? false;
        return now && !was;
    }

    /** Call once per frame AFTER all justPressed() checks to advance the snapshot. */
    commit(buttons: boolean[]): void {
        this.prev = buttons.slice();
    }

    reset(): void {
        this.prev = [];
    }
}
